import clsx from 'clsx';
import { JSX, useRef, useState } from 'react';
import ThreeDotsIcon from '../../assets/icons/three-dot-icon.svg?react';
import { useClickOutside } from '../../utils/clickOutSide';

interface DropDownOption<T = void> {
  label: string;
  onClick: (args?: T) => void;
  onOptionClickArgs?: T;
  hidden?: boolean;
  isLoading?: boolean;
}

interface DropDownProps<T = void> {
  otherClasses?: string;
  options: DropDownOption<T>[];
  additionalClasses?: string;
  disabled?: boolean;
}

const DropDown = <T,>({
  otherClasses,
  options,
  additionalClasses,
  disabled = false,
}: DropDownProps<T>): JSX.Element => {
  const dropDownClasses = clsx(otherClasses, 'group relative');
  const ref = useRef(null);
  const [toggle, setToggle] = useState(false);
  useClickOutside({ ref, setToggle });
  return (
    <div ref={ref} className={dropDownClasses}>
      <button
        disabled={disabled}
        onClick={() => setToggle(!toggle)}
        className="cursor-pointer opacity-100 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <ThreeDotsIcon className="[&>path]:fill-neutral-tones-text" />
      </button>
      {toggle && (
        <ul
          className={clsx(
            'bg-light-translucent-background ring-neutral-tones-text/35 dark:bg-dark-translucent-background absolute top-7 right-0 z-10 flex w-48 flex-col rounded-md p-2.5 ring',
            additionalClasses,
          )}
        >
          {options.map((option, index) => (
            <button
              disabled={option.isLoading}
              key={index}
              className={clsx(
                'font-inter-variable text-neutral-tones-text border-b-neutral-tones-text/35 hover:text-brand-byzantine global-transition cursor-pointer border-b py-2 text-left text-sm font-normal first:pt-0 last:border-b-0 last:pb-0 disabled:cursor-not-allowed disabled:text-neutral-600',
                option?.hidden && 'hidden',
              )}
              onClick={() => option.onClick(option.onOptionClickArgs)}
            >
              {option.label}
            </button>
          ))}
        </ul>
      )}
    </div>
  );
};

export default DropDown;
